import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { woKeys } from "@/lib/query";
import type { CreateWorkOrderInput, UpdateWorkOrderInput } from "@/schemas/work-orders";

export interface ResourcePreview {
  id: string;
  name: string;
}

export interface WorkOrderResource {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface WorkOrder {
  id: string;
  woNumber: string;
  client: string;
  jobType: string;
  billable: boolean;
  active: boolean;
  startDate: string | null;
  endDate: string | null;
  lastImportedAt: string | null;
  importedBy: string | null;
  createdAt: string;
  updatedAt: string;
  resourceCount: number;
  resourcePreview: ResourcePreview[];
}

export interface WorkOrderDetail extends WorkOrder {
  resources: WorkOrderResource[];
}

interface WorkOrdersListResponse {
  data: WorkOrder[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

interface ListParams {
  search?: string;
  status?: "active" | "archived" | "all";
  limit?: number;
  cursor?: string;
}

export function useWorkOrders(params: ListParams = {}) {
  const search = new URLSearchParams();
  if (params.search) search.set("search", params.search);
  if (params.status) search.set("status", params.status);
  if (params.limit) search.set("limit", String(params.limit));
  if (params.cursor) search.set("cursor", params.cursor);

  return useQuery({
    queryKey: woKeys.list(params as Record<string, unknown>),
    queryFn: async (): Promise<WorkOrdersListResponse> => {
      const res = await fetch(`/api/work-orders?${search.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch work orders");
      return res.json() as Promise<WorkOrdersListResponse>;
    },
  });
}

export function useWorkOrder(id: string | null, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: id ? woKeys.detail(id) : ["work-orders", "detail", "none"],
    enabled: Boolean(id) && (opts.enabled ?? true),
    queryFn: async (): Promise<WorkOrderDetail> => {
      const res = await fetch(`/api/work-orders/${id}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to fetch work order");
      }
      const json = (await res.json()) as { data: WorkOrderDetail };
      return json.data;
    },
  });
}

export function useCreateWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateWorkOrderInput) => {
      const res = await fetch("/api/work-orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to create work order");
      }
      return res.json() as Promise<{ data: WorkOrderDetail }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: woKeys.all });
      toast.success("Work order created");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useUpdateWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateWorkOrderInput }) => {
      const res = await fetch(`/api/work-orders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to update work order");
      }
      return res.json() as Promise<{ data: WorkOrderDetail }>;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: woKeys.all });
      queryClient.invalidateQueries({ queryKey: woKeys.detail(id) });
      toast.success("Work order updated");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useArchiveWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/work-orders/${id}/archive`, { method: "PATCH" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to archive work order");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: woKeys.all });
      toast.success("Work order archived");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

interface ImportWorkOrdersInput {
  records: { woNumber: string; client: string; jobType: string; billable: boolean }[];
}

export function useImportWorkOrders() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ImportWorkOrdersInput) => {
      const res = await fetch("/api/work-orders/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to import work orders");
      }
      return res.json() as Promise<{
        added: number;
        updated: number;
        skipped: number;
        errorRows: unknown[];
      }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: woKeys.all });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
